import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import {initReactI18next} from 'react-i18next'

export const DEFAULT_LANGUAGE = 'zh-CN'
export const LANGUAGE_STORAGE_KEY = 'dokimo-language'
export const SUPPORTED_LANGUAGES = ['zh-CN', 'zh-rHK', 'zh-rTW'] as const

export type DokimoLanguage = (typeof SUPPORTED_LANGUAGES)[number]

/** 临时强制展示语言；设为 null 后恢复 localStorage + 浏览器语言检测 */
export const FORCED_DISPLAY_LANGUAGE: DokimoLanguage | null = 'zh-rTW'

const LANGUAGE_MAP: Record<string, DokimoLanguage> = {
  'zh-CN': 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh-rHK': 'zh-rHK',
  'zh-HK': 'zh-rHK',
  'zh-rTW': 'zh-rTW',
  'zh-TW': 'zh-rTW',
}

const LANGUAGE_FOLDER_MAP: Record<string, DokimoLanguage> = {
  'zh-CN': 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh-rHK': 'zh-rHK',
  'zh-RHK': 'zh-rHK',
  'zh-rTW': 'zh-rTW',
  'zh-RTW': 'zh-rTW',
}

const loadResource = (language: string, namespace: string) => {
  const folder = LANGUAGE_FOLDER_MAP[language] || DEFAULT_LANGUAGE
  return import(`./locales/${folder}/${namespace}.json`)
}

export const normalizeLanguage = (language?: string | null): DokimoLanguage => {
  if (!language) {
    return DEFAULT_LANGUAGE
  }

  return LANGUAGE_MAP[language] || DEFAULT_LANGUAGE
}

const detectLanguage = (): DokimoLanguage => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE
  }

  const cachedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (cachedLanguage) {
    return normalizeLanguage(cachedLanguage)
  }

  return normalizeLanguage(window.navigator.language)
}

export const getCurrentLanguage = (): DokimoLanguage => {
  if (FORCED_DISPLAY_LANGUAGE) {
    return FORCED_DISPLAY_LANGUAGE
  }

  return detectLanguage()
}

const languageDetector = new LanguageDetector()

languageDetector.addDetector({
  name: 'dokimoBrowser',
  lookup() {
    return getCurrentLanguage()
  },
  cacheUserLanguage(language: string) {
    if (FORCED_DISPLAY_LANGUAGE) {
      return
    }

    const normalizedLanguage = normalizeLanguage(language)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage)
    }
  },
})

export const i18nInitPromise = i18next
  .use(resourcesToBackend(loadResource))
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    ...(FORCED_DISPLAY_LANGUAGE ? {lng: FORCED_DISPLAY_LANGUAGE} : {}),
    fallbackLng: FORCED_DISPLAY_LANGUAGE || DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES, 'zh-RHK', 'zh-RTW'],
    load: 'currentOnly',
    ns: 'translation',
    detection: {
      order: ['dokimoBrowser', 'navigator'],
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    returnEmptyString: false,
  })

export default i18next
